import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ProductModal from '../components/ProductModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { fetchProducts, createProduct, updateProduct, deleteProduct } from '../store/slices/productsSlice';
import { fetchCatalogs } from '../store/slices/catalogsSlice';
import { fetchCustomers } from '../store/slices/customersSlice';
import { apiService } from '../services/api';
import './Products.css';
import { useParams } from 'react-router-dom';
const Products = () => {
  const { catalogId } = useParams();
  const dispatch = useDispatch();
  const { items: products, loading, pagination } = useSelector((state) => state.products);
  const { items: catalogs } = useSelector((state) => state.catalogs);
  const { customers } = useSelector((state) => state.customers);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCatalog, setSelectedCatalog] = useState('all');
  const [sendingProduct, setSendingProduct] = useState(null);
  const [sendToPhone, setSendToPhone] = useState('');
  const [isSendingTemplate, setIsSendingTemplate] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Determine initial catalog from localStorage (set by Catalogs page "View Products")
  useEffect(() => {
    const selectedForView = localStorage.getItem('selectedCatalogForView');
    const activeCatalogId = localStorage.getItem('activeCatalogId');
    if (selectedForView) {
      setSelectedCatalog(selectedForView);
      localStorage.removeItem('selectedCatalogForView');
    } else if (activeCatalogId) {
      setSelectedCatalog(activeCatalogId);
    }
  }, []);

  // Load catalogs for the filter dropdown
  useEffect(() => {
    if (!catalogs || catalogs.length === 0) {
      dispatch(fetchCatalogs());
    }
  }, [dispatch, catalogs]);

  // Fetch products whenever filters change
  const loadProducts = useCallback(() => {
    const params = { page: 1, limit: pagination.limit, catalogId: catalogId };
    if (selectedCatalog && selectedCatalog !== 'all') params.catalogId = catalogId || selectedCatalog;
    if (selectedCategory && selectedCategory !== 'all') params.category = selectedCategory;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

    dispatch(fetchProducts(params))
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load products');
      });
  }, [dispatch, selectedCatalog, selectedCategory, debouncedSearch, pagination.limit]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product) => {
    const catalog = catalogs.find((c) => c._id === product.catalogId?.toString());
    setEditingProduct({ ...product, metaCatalogId: catalog?.metaCatalogId || '' });
    setIsModalOpen(true);
  };

  const handleDeleteProduct = (product) => {
    setProductToDelete(product);
    setIsDeleteModalOpen(true);
  };

  const handleSaveProduct = async (formData) => {
    try {
      if (editingProduct) {
        await dispatch(updateProduct({ id: editingProduct._id, data: formData })).unwrap();
        toast.success('Product updated successfully');
      } else {
        await dispatch(createProduct(formData)).unwrap();
        toast.success('Product created successfully');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to save product');
    }
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    try {
      await dispatch(deleteProduct(productToDelete._id)).unwrap();
      toast.success('Product deleted successfully');
      setIsDeleteModalOpen(false);
      setProductToDelete(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to delete product');
    }
  };

  const handleOpenSendTemplate = (product) => {
    setSendingProduct(product);
    setSendToPhone('');
    setSelectedCustomer(null);
    setCustomerSearch('');
    if (!customers || customers.length === 0) {
      dispatch(fetchCustomers({ limit: 200 }));
    }
  };

  const handleSendProductTemplate = async (e) => {
    e.preventDefault();
    const phone = selectedCustomer ? (selectedCustomer.phone || selectedCustomer.phoneNumber) : sendToPhone.trim();
    if (!phone || !sendingProduct) return;
    setIsSendingTemplate(true);
    try {
      await apiService.messages.sendProductTemplate({
        toPhoneNumber: phone,
        catalogId: sendingProduct?.metaCatalogId || undefined,
        productRetailerId: sendingProduct.retailerId || undefined,
      });
      toast.success('Product template sent successfully');
      setSendingProduct(null);
      setSendToPhone('');
      setSelectedCustomer(null);
      setCustomerSearch('');
    } catch (err) {
      toast.error(err?.message || 'Failed to send product template');
    } finally {
      setIsSendingTemplate(false);
    }
  };

  const filteredCustomers = (customers || []).filter((c) => {
    if (!customerSearch.trim()) return true;
    const q = customerSearch.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || c.phoneNumber || '').includes(q)
    );
  });

  const formatPrice = (price, currency) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(price);
    } catch {
      return `${currency || 'USD'} ${price}`;
    }
  };

  const activeCatalogId = localStorage.getItem('activeCatalogId');
  const selectedCatalogId = selectedCatalog !== 'all' ? selectedCatalog : activeCatalogId;
  const defaultCatalog = catalogs.find((c) => c._id === selectedCatalogId);
  const defaultMetaCatalogId = defaultCatalog?.metaCatalogId || '';
  const allCategories = ['all', ...new Set(products.map((p) => p.category).filter(Boolean))];

  return (
    <div className="products-container">
      <div className="products-header">
        <div className="header-content">
          <h1>Products</h1>
          <p>Manage your product inventory</p>
        </div>
        <button className="btn-add-product" onClick={handleAddProduct}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Product
        </button>
      </div>

      <div className="products-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search products by name, description, or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-select"
          >
            {allCategories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group catalog-filter">
          <label>Catalog:</label>
          <select
            value={selectedCatalog}
            onChange={(e) => setSelectedCatalog(e.target.value)}
            className="catalog-select"
          >
            <option value="all">All Catalogs</option>
            {catalogs.map(c => (
              <option key={c._id} value={c._id}>
                {c.name || c._id}
              </option>
            ))}
          </select>
          {selectedCatalog !== 'all' && selectedCatalog === activeCatalogId && (
            <span className="active-badge">Active ✅</span>
          )}
        </div>

        <div className="products-stats">
          <span className="stat-item">
            Total: <strong>{pagination.total}</strong>
          </span>
          <span className="stat-item">
            Active: <strong>{products.filter(p => p.status === 'active').length}</strong>
          </span>
          <span className="stat-item">
            Shown: <strong>{products.length}</strong>
          </span>
        </div>
      </div>

      {loading && <div className="loading-state">Loading products...</div>}

      <div className="products-grid">
        {!loading && products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No products found</h3>
            <p>Try adjusting your search or filter criteria, or add a new product</p>
            <button className="btn-add-first" onClick={handleAddProduct}>
              Add Your First Product
            </button>
          </div>
        ) : (
          products.map(product => (
            <div key={product._id} className="product-card">
              <div className="product-image">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt={product.name} className="product-image-url" />
                ) : (
                  <span className="product-image-emoji">{product.imageEmoji || '📦'}</span>
                )}
              </div>
              <div className="product-content">
                <div className="product-header">
                  <h3 className="product-name">{product.name}</h3>
                  <span className={`product-status ${product.status}`}>
                    {product.status}
                  </span>
                </div>
                <p className="product-description">{product.description}</p>
                <div className="product-details">
                  <div className="detail-item">
                    <span className="detail-label">Price:</span>
                    <div className="price-container">
                      {product.salePrice && (
                        <span className="detail-value sale-price">{formatPrice(product.salePrice, product.currency)}</span>
                      )}
                      <span className={`detail-value price ${product.salePrice ? 'original-price' : ''}`}>
                        {formatPrice(product.price, product.currency)}
                      </span>
                    </div>
                  </div>
                  {product.brand && (
                    <div className="detail-item">
                      <span className="detail-label">Brand:</span>
                      <span className="detail-value">{product.brand}</span>
                    </div>
                  )}
                  {product.sku && (
                    <div className="detail-item">
                      <span className="detail-label">SKU:</span>
                      <span className="detail-value">{product.sku}</span>
                    </div>
                  )}
                  {product.retailerId && (
                    <div className="detail-item">
                      <span className="detail-label">Retailer ID:</span>
                      <span className="detail-value">{product.retailerId}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Stock:</span>
                    <span className={`detail-value ${product.stock < 10 ? 'low-stock' : ''}`}>
                      {product.stock} units
                    </span>
                  </div>
                  {product.category && (
                    <div className="detail-item">
                      <span className="detail-label">Category:</span>
                      <span className="detail-value category">{product.category}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="product-actions">
                <button
                  className="action-btn send-btn"
                  onClick={() => handleOpenSendTemplate(product)}
                  title="Send Product Template"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
                <button
                  className="action-btn edit-btn"
                  onClick={() => handleEditProduct(product)}
                  title="Edit Product"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
                <button
                  className="action-btn delete-btn"
                  onClick={() => handleDeleteProduct(product)}
                  title="Delete Product"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ProductModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
        product={editingProduct}
        defaultCatalogId={defaultMetaCatalogId}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProductToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={productToDelete?.name || ''}
        type="product"
      />

      {sendingProduct && (
        <div className="send-template-overlay" onClick={() => setSendingProduct(null)}>
          <div className="send-template-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Send Product to Customer</h3>
            <p className="send-template-product-name">{sendingProduct.name}</p>
            <form onSubmit={handleSendProductTemplate}>
              <label>Select Customer</label>
              <input
                type="text"
                placeholder="Search by name or phone..."
                value={customerSearch}
                onChange={(e) => { setCustomerSearch(e.target.value); setSelectedCustomer(null); }}
                className="send-template-input"
                autoFocus
              />
              {selectedCustomer ? (
                <div className="selected-customer-badge">
                  <span>✓ {selectedCustomer.name} ({selectedCustomer.phone || selectedCustomer.phoneNumber})</span>
                  <button type="button" onClick={() => setSelectedCustomer(null)}>✕</button>
                </div>
              ) : (
                <div className="customer-list-dropdown">
                  {filteredCustomers.length === 0 ? (
                    <div className="customer-list-empty">No customers found</div>
                  ) : (
                    filteredCustomers.slice(0, 8).map((c) => (
                      <div
                        key={c._id}
                        className="customer-list-item"
                        onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); }}
                      >
                        <span className="customer-list-name">{c.name}</span>
                        <span className="customer-list-phone">{c.phone || c.phoneNumber}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
              <div className="send-template-actions">
                <button type="button" className="btn-cancel" onClick={() => setSendingProduct(null)}>Cancel</button>
                <button
                  type="submit"
                  className="btn-send"
                  disabled={isSendingTemplate || (!selectedCustomer && !sendToPhone.trim())}
                >
                  {isSendingTemplate ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
